import React, { useState, useEffect } from 'react';
import { Alert, Card, CardContent, TextField, MenuItem, Select } from '@mui/material';
import * as yup from 'yup';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../../redux/slices/notificationSlice';
import CustomButton from '../../../componentsII/Buttons';
import api from '../../../axios/api';

// Define validation schema
const schema = yup.object().shape({
  sector: yup.string().required('Sector is required'),
  industry: yup.string().required('Industry is required'),
  subIndustryName: yup
    .string()
    .required('Sub-industry name is required')
    .min(2, 'Sub-industry name must be at least 2 characters long')
    .max(50, 'Sub-industry name must be less than 50 characters')
    .trim()
});

const AddUpdateSubIndustryModal = ({ refetch, onClose, SubIndustryId = null, initialData = null }) => {
  const dispatch = useDispatch();

  // Form states
  const [subIndustryName, setSubIndustryName] = useState(initialData?.sub_industry_name || '');
  const [selectedIndustry, setSelectedIndustry] = useState(initialData?.industry_name || '');
  const [selectedSector, setSelectedSector] = useState(initialData?.sector_name || '');
  const [industries, setIndustries] = useState([]);
  const [sectors, setSectors] = useState([]);

  // Filtered industries based on selected sector
  const [filteredIndustries, setFilteredIndustries] = useState([]);

  // UI states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({
    subIndustryName: '',
    industry: '',
    sector: '',
    general: ''
  });

  // Validate form
  const validateForm = async () => {
    try {
      await schema.validate({
        sector: selectedSector,
        industry: selectedIndustry,
        subIndustryName
      }, { abortEarly: false });
      return true;
    } catch (err) {
      const newErrors = {};
      err.inner.forEach((error) => {
        newErrors[error.path] = error.message;
      });
      setErrors(prevErrors => ({
        ...prevErrors,
        ...newErrors
      }));
      return false;
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({ subIndustryName: '', industry: '', sector: '', general: '' });

    const isValid = await validateForm();
    if (!isValid) return;

    try {
      setIsSubmitting(true);

      const selectedIndustryObj = industries.find(industry => industry.industry_name === selectedIndustry);

      const payload = {
        SubIndustryId: SubIndustryId || 0,
        industry_id: selectedIndustryObj?.id,
        SubIndustryName: subIndustryName.trim(),
      };

      const response = await api.post('/admin/CreateUpdateSubIndustry', payload);

      if (response.data.status === '200') {
        dispatch(showNotification({
          message: response.data.message,
          type: "success"
        }));
        onClose();
        refetch();
      } else {
        setErrors(prev => ({
          ...prev,
          general: response.data.message || 'Something went wrong'
        }));
      }
    } catch (err) {
      setErrors(prev => ({
        ...prev,
        general: err.message || 'An error occurred while saving'
      }));
      dispatch(showNotification({
        message: err.message || 'An error occurred',
        type: "error"
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle sector change
  const handleSectorChange = (e) => {
    const selectedSector = e.target.value;
    setSelectedSector(selectedSector);
    setSelectedIndustry(''); // Reset industry when sector changes
    setErrors(prev => ({ ...prev, sector: '', industry: '' }));

    // Filter industries based on selected sector
    if (selectedSector) {
      const filtered = industries.filter(industry => industry.sector_id === sectors.find(s => s.sector_name === selectedSector)?.id);
      setFilteredIndustries(filtered);
    } else {
      setFilteredIndustries([]);
    }
  };

  // Handle industry change
  const handleIndustryChange = (e) => {
    setSelectedIndustry(e.target.value);
    setErrors(prev => ({ ...prev, industry: '' }));
  };

  // Handle sub-industry name change
  const handleSubIndustryNameChange = (e) => {
    setSubIndustryName(e.target.value);
    setErrors(prev => ({ ...prev, subIndustryName: '' }));
  };

  // Fetch industries
  const getIndustry = async () => {
    try {
      const response = await api.get('/admin/getIndustry');
      if (response.data.data) {
        setIndustries(response.data.data);

        // If in edit mode, filter industries based on initialData's sector
        if (initialData) {
          const filtered = response.data.data.filter(industry => industry.sector_id === sectors.find(s => s.sector_name === initialData.sector_name)?.id);
          setFilteredIndustries(filtered);
        }
      }
    } catch (err) {
      dispatch(showNotification({
        message: 'Failed to fetch industries',
        type: "error"
      }));
    }
  };

  // Fetch sectors
  const getSectors = async () => {
    try {
      const response = await api.get('/admin/getSector');
      if (response.data.data) {
        setSectors(response.data.data);
      }
    } catch (err) {
      dispatch(showNotification({
        message: 'Failed to fetch sectors',
        type: "error"
      }));
    }
  };

  useEffect(() => {
    getSectors();
    getIndustry();
  }, []);

  useEffect(() => {
    if (initialData && industries.length > 0 && sectors.length > 0) {
      // Set the selected sector based on initialData
      setSelectedSector(initialData.sector_name);

      // Filter industries based on the selected sector
      const filtered = industries.filter(industry => industry.sector_id === sectors.find(s => s.sector_name === initialData.sector_name)?.id);
      setFilteredIndustries(filtered);

      // Set the selected industry based on initialData
      setSelectedIndustry(initialData.industry_name);
    }
  }, [initialData, industries, sectors]);

  return (
    <Card className="w-full mx-auto">
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="sector" className="text-sm font-medium">
              Sectors
            </label>
            <Select
              id="sector"
              value={selectedSector}
              onChange={handleSectorChange}
              placeholder="Select Sector"
              disabled={isSubmitting}
              size="small"
              error={Boolean(errors.sector)}
              className="w-full"
            >
              {sectors.map((sector) => (
                <MenuItem key={sector.id} value={sector.sector_name}>
                  {sector.sector_name}
                </MenuItem>
              ))}
            </Select>
            {errors.sector && (
              <p className="text-sm text-red-500 mt-1">{errors.sector}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="industry" className="text-sm font-medium">
              Industry
            </label>
            <Select
              id="industry"
              value={selectedIndustry}
              onChange={handleIndustryChange}
              placeholder="Select Industry"
              disabled={isSubmitting || !selectedSector}
              size="small"
              error={Boolean(errors.industry)}
              className="w-full"
            >
              {filteredIndustries.map((industry) => (
                <MenuItem key={industry.id} value={industry.industry_name}>
                  {industry.industry_name}
                </MenuItem>
              ))}
            </Select>
            {errors.industry && (
              <p className="text-sm text-red-500 mt-1">{errors.industry}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="subIndustryName" className="text-sm font-medium">
              Sub-Industry Name
            </label>
            <TextField
              id="subIndustryName"
              value={subIndustryName}
              onChange={handleSubIndustryNameChange}
              placeholder="Enter sub-industry name"
              disabled={isSubmitting}
              size="small"
              error={Boolean(errors.subIndustryName)}
              className="w-full"
            />
            {errors.subIndustryName && (
              <p className="text-sm text-red-500 mt-1">{errors.subIndustryName}</p>
            )}
          </div>

          <div className="flex justify-end gap-5 items-center mt-5">
            <CustomButton
              className="bg-gray-500 hover:bg-gray-700 text-white"
              text="Cancel"
              click={onClose}
              disabled={isSubmitting}
            />
            <CustomButton
              className="bg-green-600 hover:bg-green-700 text-white"
              text={SubIndustryId ? "Update" : "Submit"}
              type="submit"
              disabled={isSubmitting}
            />
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default AddUpdateSubIndustryModal;